/**
 * Where an act chosen on the Kanban layout lands in the case panel (2026-10-13).
 *
 * The case panel marks two sections with `data-case-anchor`: the Submission
 * section ("packet") and the approval evidence ("approval"). When the board
 * asks for one -- a refused move's "Open the case at the packet", or a drop on
 * Approved -- the panel scrolls it into view (instantly under reduced motion)
 * and puts focus on the act's WORDS, never on a control that commits (round 2,
 * 2026-10-14: landing on "Approve" let a key repeat after a keyboard drop
 * approve): the confirmation's sentence for `approve`, the section's heading
 * otherwise. And only while focus is still where the act left it -- the page
 * body, the card's grip or Move button, the dock heading (focusRule.mayTakeFocus):
 * once the person has moved on, nothing takes it back. Nothing here records anything.
 *
 * Focus is taken AFTER the board's own (OperationalKanban DOCK_FOCUS_DELAY_MS
 * puts it on the dock heading once dnd-kit has restored it to the grip), and
 * retried while the case is still arriving (verification 2026-10-14: a keyboard
 * drop's focus restore beat ApproveAct's autoFocus).
 */
import { useEffect } from 'react';
import type { CaseFocus } from './umedApi';
import { mayTakeFocus, isCommitControl } from '../../components/board/kanban/focusRule';

export const CASE_ANCHOR: Partial<Record<CaseFocus['kind'], string>> = {
  packet: 'packet',
  'approval-evidence': 'approval',
  approve: 'approval',
  // 2026-10-28: the row's "Record the review" lands on the case's Record a review section.
  review: 'review',
  // 2026-10-28: an open correction held for its terms or total lands on the case's Terms section (Record terms).
  terms: 'terms',
};
/** First look: after the board's dock focus (80 ms). Then every RETRY_MS, up to TRIES. */
export const ANCHOR_FIRST_MS = 160;
const RETRY_MS = 100;
const TRIES = 20;

function reducedMotion(): boolean {
  try { return typeof window.matchMedia === 'function' && window.matchMedia('(prefers-reduced-motion: reduce)').matches; } catch { return false; }
}

/**
 * What an act lands on inside its section: words, never a control. Approve's confirmation sentence
 * (`data-act-focus="approve"`, ApproveAct), else the section's heading, else the section itself. Exported for tests.
 */
export function targetIn(el: HTMLElement, kind: CaseFocus['kind']): HTMLElement | null {
  const candidate = kind === 'approve'
    ? el.querySelector<HTMLElement>('[data-act-focus="approve"]')
    : (el.querySelector<HTMLElement>('h3, h2') || el);
  return candidate && !isCommitControl(candidate) ? candidate : null;
}

export function useCaseAnchor(focus: CaseFocus | null | undefined, caseId: string | null): void {
  const kind = focus ? focus.kind : null;
  const n = focus ? focus.n : 0;
  useEffect(() => {
    const name = kind ? CASE_ANCHOR[kind] : undefined;
    if (!kind || !name || !caseId || typeof document === 'undefined') return undefined;
    let tries = 0;
    let scrolled = false;
    let t: number | null = null;
    const look = () => {
      t = null;
      tries += 1;
      const el = document.querySelector<HTMLElement>(`[data-case-anchor="${name}"]`);
      if (el && !scrolled && typeof el.scrollIntoView === 'function') { el.scrollIntoView({ block: 'start', behavior: reducedMotion() ? 'auto' : 'smooth' }); scrolled = true; }
      const target = el ? targetIn(el, kind) : null;
      if (target) {
        // The person has moved on (the search box, a field): leave them there.
        if (!mayTakeFocus(document.activeElement)) return;
        if (!target.hasAttribute('tabindex')) target.setAttribute('tabindex', '-1');
        target.focus({ preventScroll: true });
        return;
      }
      if (tries < TRIES) t = window.setTimeout(look, RETRY_MS);
    };
    t = window.setTimeout(look, ANCHOR_FIRST_MS);
    return () => { if (t !== null) window.clearTimeout(t); };
  }, [kind, n, caseId]);
}
